/**
 * NavegacaoEtapa — chevrons "‹ Título ›" coladinhos no título da página,
 * para percorrer as etapas do fluxo legado (problema #7 / F10).
 *
 * Uso: envolve o próprio <h1>/Titulo da página, preservando o estilo dele:
 *
 *   <NavegacaoEtapa>
 *     <PageTitle>Definição de Acabamentos</PageTitle>
 *   </NavegacaoEtapa>
 *
 * Auto-localiza pela rota (useLocation): fora do fluxo, renderiza só o
 * título (sem chevrons). Por isso pode ser colocado dentro de componentes
 * compartilhados como PainelTabela — só aparece nas 15 telas do fluxo.
 *
 * A sequência canônica vive em ./etapas.js.
 */
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

import { useAuth } from "../../context/AuthContext";
import { vizinhosDaRota } from "./etapas";
import { Chevron, ChevronGroup, Contador, Eyebrow, Linha, TituloBloco } from "./styles.jsx";

function NavegacaoEtapa({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasAllPermissions, isMaster } = useAuth();

  // P3 (2026-06-18): uma etapa eh acessivel se o usuario eh Master, se ela nao
  // exige permissao, ou se ele tem a permissao `recurso:view` dela. As setas
  // operam SO sobre as etapas acessiveis (pulam as bloqueadas) e cada seta so
  // aparece quando ha de fato um destino acessivel naquele sentido.
  const temAcesso = (etapa) =>
    isMaster ||
    !etapa?.requires ||
    (typeof hasAllPermissions === "function" && hasAllPermissions([etapa.requires]));

  const info = vizinhosDaRota(location.pathname, temAcesso);
  // Fora do fluxo: devolve só o título, sem envolver nem adicionar chevrons.
  if (!info) return children ?? null;

  const { anterior, proxima, indice, total } = info;

  return (
    <Linha>
      {/* Sempre as DUAS caixas (mockup); sem destino acessível = disabled.
          O gate P3 continua: navega só para etapa acessível. */}
      <ChevronGroup>
        <Chevron
          type="button"
          disabled={!anterior}
          onClick={anterior ? () => navigate(anterior.to) : undefined}
          title={anterior ? `Etapa anterior: ${anterior.label}` : undefined}
          aria-label={
            anterior
              ? `Ir para etapa anterior: ${anterior.label}`
              : "Sem etapa anterior"
          }
        >
          <FiChevronLeft aria-hidden="true" />
        </Chevron>
        <Chevron
          type="button"
          disabled={!proxima}
          onClick={proxima ? () => navigate(proxima.to) : undefined}
          title={proxima ? `Próxima etapa: ${proxima.label}` : undefined}
          aria-label={
            proxima ? `Ir para próxima etapa: ${proxima.label}` : "Sem próxima etapa"
          }
        >
          <FiChevronRight aria-hidden="true" />
        </Chevron>
      </ChevronGroup>

      <TituloBloco>
        <Eyebrow>
          <span>PCP</span>
          <span className="sep">/</span>
          <span>
            Etapa {indice + 1} de {total}
          </span>
        </Eyebrow>
        {children}
      </TituloBloco>
      <Contador aria-label={`Etapa ${indice + 1} de ${total}`}>
        {indice + 1}/{total}
      </Contador>
    </Linha>
  );
}

export default NavegacaoEtapa;
